/**
 * API client per il log audit (E7.6).
 * GET /api/products/audit/events → AuditQueryResult
 */
import { createServiceClient } from '../../api/client.js';

export interface AuditEventRow {
  eventId: string;
  timestamp: string;
  actor: string;
  action: string;
  entityType: string;
  entityId: string;
  service: string;
  correlationId?: string;
  /** Dettaglio della modifica (solo alcuni eventi lo valorizzano). */
  before?: unknown;
  after?: unknown;
}

export interface AuditQueryResult {
  items: AuditEventRow[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AuditQueryParams {
  page?: number;
  pageSize?: number;
  actor?: string;
  /** Multi-value: azioni selezionate (OR). Vuoto = tutte. */
  actions?: string[];
  /** Multi-value: tipi entità selezionati (OR). Vuoto = tutti. */
  entityTypes?: string[];
  service?: string;
  from?: string;
  to?: string;
  /** Id esatto dell'entità: storico di una singola entità. */
  entityId?: string;
  /** Prefisso dell'id entità (es. `<prodotto>@` = tutte le release del prodotto). */
  entityIdPrefix?: string;
}

const productApi = createServiceClient('product');

export function fetchAuditEvents(params: AuditQueryParams = {}): Promise<AuditQueryResult> {
  const qs = new URLSearchParams();
  const { actions, entityTypes, ...rest } = params;
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  if (actions?.length) qs.set('action', actions.join(','));
  if (entityTypes?.length) qs.set('entityType', entityTypes.join(','));
  const q = qs.toString();
  return productApi<AuditQueryResult>(`/audit/events${q ? `?${q}` : ''}`);
}

/** Tipi entità e servizi presenti nel log (filtri della pagina Log Audit). */
export interface AuditFacets {
  entityTypes: string[];
  services: string[];
}

export function fetchAuditFacets(): Promise<AuditFacets> {
  return productApi<AuditFacets>('/audit/facets');
}

/** Una "serie" dello storico di un'entità: tipi entità + id esatto o prefisso dell'id. */
export interface AuditHistoryQuery {
  entityTypes: string[];
  entityId?: string;
  entityIdPrefix?: string;
  /** Scarta gli eventi precedenti a questo istante (ISO): serie condivise tra più entità. */
  from?: string;
}

/** Eventi per pagina letti per serie (massimo consentito da GET /audit/events). */
export const HISTORY_PAGE_SIZE = 200;

/** Tetto di pagine per serie (difesa da loop): 50 × 200 = 10.000 eventi. */
const MAX_HISTORY_PAGES = 50;

/** Tutti gli eventi di una serie, pagina per pagina: lo storico non si tronca a 200 eventi. */
async function fetchAllPages(q: AuditHistoryQuery): Promise<AuditEventRow[]> {
  const eventi: AuditEventRow[] = [];
  for (let page = 1; page <= MAX_HISTORY_PAGES; page++) {
    const res = await fetchAuditEvents({
      entityTypes: q.entityTypes,
      entityId: q.entityId,
      entityIdPrefix: q.entityIdPrefix,
      from: q.from,
      pageSize: HISTORY_PAGE_SIZE,
      page,
    });
    eventi.push(...res.items);
    if (res.items.length < HISTORY_PAGE_SIZE || eventi.length >= res.total) break;
  }
  return eventi;
}

/**
 * Storico audit unito di più serie (es. ticket + suo subjectRef), dal più recente, senza
 * duplicati. Richiede il permesso VIEW_AUDIT_LOG (come la pagina Log Audit).
 */
export async function fetchAuditHistory(queries: AuditHistoryQuery[]): Promise<AuditEventRow[]> {
  const serie = await Promise.all(queries.map((q) => fetchAllPages(q)));
  const visti = new Set<string>();
  const eventi: AuditEventRow[] = [];
  for (const ev of serie.flat()) {
    if (visti.has(ev.eventId)) continue;
    visti.add(ev.eventId);
    eventi.push(ev);
  }
  return eventi.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
